import {
  DrawerContentProps,
  DrawerPositionerProps,
  DrawerRootProps,
  UseDisclosureReturn,
  useBreakpointValue,
  useMediaQuery
} from "@chakra-ui/react"
import { AUGMENTATION_PANEL_TYPOGRAPHY_CSS } from "./augmentationPanelStyles"
import { useMergedDisclosure } from "./utils"

// type PanelSize = "xs" | "sm" | "md"
type PanelSizeToken = Extract<DrawerRootProps["size"], "xs" | "sm" | "md">
type PanelResolvedSize = PanelSizeToken | "full"
type PanelSize = PanelSizeToken | Partial<Record<string, PanelResolvedSize>>

// Use these drawer sizes based on media queries.
// Note: using useMediaQuery instead of useBreakpointValue for
// more control over the breakpoints.
// If desired, this could be refactored to a reusable hook that
// accepts a breakpoint map.
export function usePanelSize(options?: {
  size?: PanelSize
  allowOverlay?: boolean
}) {
  const size = options?.size || "md"
  const allowOverlay = options?.allowOverlay || false

  // Media query breakpoints.
  const lg = "(min-width: 1001px)"
  const md = "(min-width: 501px)"
  const sm = "(min-width: 0px)"

  // Order matters; largest screensize to smallest.
  const breakpointsMap: Record<PanelSizeToken, Record<string, string>> = {
    xs: { [lg]: "xs", [md]: "xs", [sm]: allowOverlay ? "full" : "xs" },
    sm: { [lg]: "sm", [md]: "xs", [sm]: allowOverlay ? "full" : "xs" },
    md: { [lg]: "md", [md]: "sm", [sm]: allowOverlay ? "full" : "xs" }
  }

  const objectSize = typeof size === "object" ? size : { base: "md" }
  const stringSize = typeof size === "string" ? size : "md"

  // Get the breakpoints (list of booleans).
  const breakpoints = useMediaQuery(Object.keys(breakpointsMap[stringSize]))

  // Get the index of the first breakpoint that is true.
  // (This is why order matters for breakpointsMap.)
  const breakpointIndex = breakpoints.findIndex((breakpoint) => breakpoint)

  // The breakpointIndex corresponds to the value in breakpointsMap.
  const stringBreakpointValue = Object.values(breakpointsMap[stringSize])[
    breakpointIndex
  ]

  const responsiveSize = useBreakpointValue(objectSize)

  const objectBreakpointValue = responsiveSize || objectSize.base || "md"

  return (
    typeof size === "string" ? stringBreakpointValue : objectBreakpointValue
  ) as DrawerRootProps["size"]
}

// How much room the panel takes within its current size. A destination asks for
// this; it never changes how the panel relates to the page behind it (that's the
// panel's size/mode). Overlay and full sizes already own the whole area, so they
// ignore it.
export type AugmentationPanelWidth = "default" | "wide"

const PANEL_WIDTH_TOKENS: Record<AugmentationPanelWidth, string> = {
  default: "augmentationPanel",
  wide: "augmentationPanelWide"
}

// Narrowest to widest.
const PANEL_WIDTH_SCALE: AugmentationPanelWidth[] = ["default", "wide"]

// Destinations nest, so a panel can hold several requests at once. The widest
// wins, which lets a parent widen its whole subtree with one declaration and
// keeps the width stable while drilling in and out of it.
export function resolvePanelWidth(
  requests: (AugmentationPanelWidth | undefined)[]
): AugmentationPanelWidth {
  return requests.reduce<AugmentationPanelWidth>((widest, request) => {
    const width = request ?? "wide"
    return PANEL_WIDTH_SCALE.indexOf(width) > PANEL_WIDTH_SCALE.indexOf(widest)
      ? width
      : widest
  }, "wide")
}

export type UseAugmentationPanelProps = {
  // Offset from all sides of screen, in pixels.
  gutter?: number
  contentPadding?: DrawerContentProps["padding"]
  offset?: {
    top?: number
    right?: number
    bottom?: number
  }
  allowOverlay?: boolean
  size?: PanelSize
  width?: AugmentationPanelWidth
  disclosure?: UseDisclosureReturn
}

// TODO: support opening panel from the left side as well.
function useAugmentationPanel(props: UseAugmentationPanelProps) {
  const { offset, allowOverlay, size: panelSizeToken } = props
  const width = props.width ?? "default"
  // Use nullish coalescing so an explicit 0 gutter is respected.
  const gutter = props.gutter ?? 10
  const contentPadding = props.contentPadding ?? "20px 15px"

  const size = usePanelSize({
    allowOverlay,
    size: panelSizeToken
  })

  const { open, onClose } = useMergedDisclosure(props.disclosure, {
    open: props.disclosure?.open
  })

  // Combined offset with gutter.
  const combinedOffset = {
    top: `${(offset?.top || 0) + gutter}px`,
    bottom: `${(offset?.bottom || 0) + gutter}px`,
    right: `${(offset?.right || 0) + gutter}px`
  }

  // Styling the drawer positioner padding conditionally based on drawer size.
  const positionerPaddingProps: Partial<DrawerPositionerProps> =
    // When full, we respect the left and right gutter but the top and bottom offsets.
    size === "full"
      ? {
          paddingTop: combinedOffset.top,
          paddingBottom: combinedOffset.bottom,
          paddingRight: `${gutter}px`,
          paddingLeft: `${gutter}px`
        }
      : {
          paddingTop: combinedOffset.top,
          paddingBottom: combinedOffset.bottom,
          // Only difference is to use the combined offset on the right side.
          paddingRight: combinedOffset.right,
          paddingLeft: `${gutter}px`
        }

  function getDrawerProps(): Partial<DrawerRootProps> {
    return {
      open: open,
      onOpenChange: (e: { open: boolean }) => !e.open && onClose(),
      // Chakra 3 doesn't recognize the variant prop it would seem
      // variant: "aside",
      size: size,
      closeOnInteractOutside: false,
      // Disable the default escape behavior
      // so we can implement conditional escape behavior,
      // such as navigating parent levels and closing internal dialogs.
      closeOnEscape: false,
      modal: false
    }
  }

  function getDrawerPositionerProps(): Partial<DrawerPositionerProps> {
    return {
      pointerEvents: "none",
      ...positionerPaddingProps
    }
  }

  function getDrawerContentProps(): Partial<DrawerContentProps> {
    // At full size the panel covers the page rather than sitting beside it,
    // which is where its surface has to close up and carry the reading itself.
    const isOverlaying = size === "full"
    const widthToken = isOverlaying ? "full" : PANEL_WIDTH_TOKENS[width]

    return {
      className: "AugmentationPanel",
      maxH: "full",
      maxW: widthToken,
      w: widthToken,
      transitionProperty: "width, max-width",
      // Most of the distance is covered in the first third, so it reads as
      // snappy while still having room to settle rather than stop.
      transitionDuration: "slow",
      transitionTimingFunction: "ease-in-smooth",
      bg: isOverlaying
        ? "augmentationPanel.surfaceOverlay"
        : "augmentationPanel.surface",
      backdropFilter: isOverlaying ? "blur(20px)" : "blur(2.5px)",
      borderRadius: "augmentationPanel",
      boxShadow: "augmentationPanel",
      overflow: "clip",
      padding: contentPadding,
      css: AUGMENTATION_PANEL_TYPOGRAPHY_CSS
    }
  }

  return {
    getDrawerProps,
    getDrawerPositionerProps,
    getDrawerContentProps
  }
}

export default useAugmentationPanel
